// Camera-shift detection. At calibration we keep small grayscale patches of
// static scenery (tape ends, textured background). During practice the same
// patches are looked for again; if they've moved or changed, the floor mapping
// is no longer valid and the rider is asked to recalibrate.

import type { Vec2 } from '../core/geometry';
import { lmVis, lmX, lmY, NUM_LANDMARKS } from '../core/pose';
import type { Anchor } from './db';

export const ANCHOR_WIDTH = 320;
const HALF = 8; // 17×17 patches
const SEARCH = 4;

export interface GrayFrame {
  data: Uint8Array;
  w: number;
  h: number;
}

export function grayFrame(src: CanvasImageSource, srcW: number, srcH: number): GrayFrame {
  const w = ANCHOR_WIDTH;
  const h = Math.round((srcH / srcW) * w);
  const c = document.createElement('canvas');
  c.width = w;
  c.height = h;
  const ctx = c.getContext('2d', { willReadFrequently: true })!;
  ctx.drawImage(src, 0, 0, w, h);
  const rgba = ctx.getImageData(0, 0, w, h).data;
  const data = new Uint8Array(w * h);
  for (let i = 0; i < w * h; i++) data[i] = (rgba[i * 4] * 77 + rgba[i * 4 + 1] * 150 + rgba[i * 4 + 2] * 29) >> 8;
  return { data, w, h };
}

function patchAt(f: GrayFrame, cx: number, cy: number): Uint8Array | null {
  if (cx - HALF < 0 || cy - HALF < 0 || cx + HALF >= f.w || cy + HALF >= f.h) return null;
  const n = 2 * HALF + 1;
  const out = new Uint8Array(n * n);
  for (let y = 0; y < n; y++) for (let x = 0; x < n; x++) out[y * n + x] = f.data[(cy - HALF + y) * f.w + (cx - HALF + x)];
  return out;
}

function stats(p: Uint8Array) {
  let s = 0;
  let s2 = 0;
  for (const v of p) {
    s += v;
    s2 += v * v;
  }
  const m = s / p.length;
  return { mean: m, sd: Math.sqrt(Math.max(0, s2 / p.length - m * m)) };
}

function ncc(a: Uint8Array, b: Uint8Array): number {
  const sa = stats(a);
  const sb = stats(b);
  if (sa.sd < 1 || sb.sd < 1) return 0;
  let acc = 0;
  for (let i = 0; i < a.length; i++) acc += (a[i] - sa.mean) * (b[i] - sb.mean);
  return acc / (a.length * sa.sd * sb.sd);
}

export interface Box {
  x0: number;
  y0: number;
  x1: number;
  y1: number;
}

/** Bounding box of a detected person (normalized), padded. */
export function personBox(lm: Float32Array | null, pad = 0.08): Box | null {
  if (!lm) return null;
  let x0 = 1, y0 = 1, x1 = 0, y1 = 0;
  for (let i = 0; i < NUM_LANDMARKS; i++) {
    if (lmVis(lm, i) < 0.2) continue;
    x0 = Math.min(x0, lmX(lm, i));
    x1 = Math.max(x1, lmX(lm, i));
    y0 = Math.min(y0, lmY(lm, i));
    y1 = Math.max(y1, lmY(lm, i));
  }
  return x1 > x0 ? { x0: x0 - pad, y0: y0 - pad, x1: x1 + pad, y1: y1 + pad } : null;
}

const inBox = (p: Vec2, b: Box) => p.x >= b.x0 && p.x <= b.x1 && p.y >= b.y0 && p.y <= b.y1;

/** Anchors at given points (e.g. tape ends) plus well-textured background spots outside `exclude`. */
export function makeAnchors(f: GrayFrame, points: Vec2[], exclude: Box[], extra = 5): Anchor[] {
  const out: Anchor[] = [];
  const add = (x: number, y: number, minSd: number, kind: 'tape' | 'scene') => {
    const p = patchAt(f, Math.round(x * f.w), Math.round(y * f.h));
    if (!p || stats(p).sd < minSd) return false;
    out.push({ x, y, patch: p, kind });
    return true;
  };
  for (const pt of points) add(pt.x, pt.y, 6, 'tape');
  // Rank a grid of candidate spots by texture, keep the best spread-out ones.
  const cands: { x: number; y: number; sd: number }[] = [];
  for (let gy = 0.08; gy < 0.95; gy += 0.07) {
    for (let gx = 0.05; gx < 0.96; gx += 0.05) {
      if (exclude.some((b) => inBox({ x: gx, y: gy }, b))) continue;
      const p = patchAt(f, Math.round(gx * f.w), Math.round(gy * f.h));
      if (p) cands.push({ x: gx, y: gy, sd: stats(p).sd });
    }
  }
  cands.sort((a, b) => b.sd - a.sd);
  let added = 0;
  for (const c of cands) {
    if (added >= extra || c.sd < 10) break;
    if (out.some((a) => Math.hypot(a.x - c.x, a.y - c.y) < 0.15)) continue;
    if (add(c.x, c.y, 10, 'scene')) added++;
  }
  return out;
}

/** Re-captures each anchor's patch from the current view (after the rider accepts a moved camera). */
export function refreshAnchors(f: GrayFrame, anchors: readonly Anchor[], exclude: Box[]): Anchor[] {
  return anchors.map((a) => {
    if (exclude.some((b) => inBox(a, b))) return a;
    const p = patchAt(f, Math.round(a.x * f.w), Math.round(a.y * f.h));
    return p ? { ...a, patch: p } : a;
  });
}

export interface AnchorCheck {
  checked: number;
  moved: number;
}

/** Looks for each anchor near its original spot; anchors covered by the rider are skipped. */
export function checkAnchors(f: GrayFrame, anchors: readonly Anchor[], exclude: Box[]): AnchorCheck {
  let checked = 0;
  let moved = 0;
  for (const a of anchors) {
    if (exclude.some((b) => inBox(a, b))) continue;
    const cx = Math.round(a.x * f.w);
    const cy = Math.round(a.y * f.h);
    let best = -1;
    let bestOff = 0;
    for (let dy = -SEARCH; dy <= SEARCH; dy++) {
      for (let dx = -SEARCH; dx <= SEARCH; dx++) {
        const p = patchAt(f, cx + dx, cy + dy);
        if (!p) continue;
        const s = ncc(a.patch, p);
        if (s > best) {
          best = s;
          bestOff = Math.hypot(dx, dy);
        }
      }
    }
    checked++;
    if (best < 0.6 || bestOff >= 3) moved++;
  }
  return { checked, moved };
}

/** True when the evidence says the camera has shifted. */
export const cameraMoved = (c: AnchorCheck) => c.checked >= 2 && c.moved >= Math.max(2, Math.ceil(c.checked * 0.6));
